// WebexIntegrationSettings.jsx
import React, { useState, useEffect } from "react";
import {
  Shield,
  AlertCircle,
  CheckCircle,
  Loader,
  ExternalLink,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

const WebexIntegrationSettings = () => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchStatus();

    const params = new URLSearchParams(window.location.search);
    const webexResult = params.get("webex");
    if (webexResult === "success") {
      setMessage({ type: "success", text: "Webex connected successfully!" });
      window.history.replaceState({}, "", window.location.pathname);
      fetchStatus();
    } else if (webexResult === "error") {
      const reason = params.get("reason") || "unknown error";
      setMessage({ type: "error", text: `Webex connection failed: ${reason}` });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await apiService.getWebexIntegrationStatus();
      setStatus(response.data);
    } catch (error) {
      console.error("Error fetching Webex status:", error);
    }
  };

  const handleConnect = async () => {
    try {
      setLoading(true);
      setMessage(null);
      const response = await apiService.getWebexAuthUrl();
      window.location.href = response.authUrl;
    } catch (error) {
      setMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          "Failed to initiate Webex connection",
      });
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (
      !window.confirm(
        "Are you sure you want to disconnect the Webex integration?",
      )
    )
      return;

    try {
      setLoading(true);
      setMessage(null);
      await apiService.disconnectWebexIntegration();
      setMessage({ type: "success", text: "Webex integration disconnected" });
      fetchStatus();
    } catch (error) {
      setMessage({
        type: "error",
        text: "Failed to disconnect Webex integration",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`rounded-lg border p-6 transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white">
          <Shield size={20} />
        </div>
        <div>
          <h2
            className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            Webex Calling Integration
          </h2>
          <p
            className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            Connect your organisation's Webex account to enable call pop-ups and
            automatic call logging
          </p>
        </div>
      </div>

      {/* Feedback banner */}
      {message && (
        <div
          className={`flex items-center gap-3 p-4 rounded-lg mb-6 ${
            message.type === "success"
              ? isDarkMode
                ? "bg-green-500/10 border border-green-500/30 text-green-400"
                : "bg-green-50 border border-green-200 text-green-600"
              : isDarkMode
                ? "bg-red-500/10 border border-red-500/30 text-red-400"
                : "bg-red-50 border border-red-200 text-red-600"
          }`}
        >
          {message.type === "success" ? (
            <CheckCircle size={20} />
          ) : (
            <AlertCircle size={20} />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Connected state */}
      {status?.configured ? (
        <div className="space-y-4">
          <div
            className={`p-4 rounded-lg ${isDarkMode ? "bg-green-500/10 border border-green-500/30" : "bg-green-50 border border-green-200"}`}
          >
            <div className="flex items-center gap-2 text-green-400 mb-3">
              <CheckCircle size={18} />
              <span className="font-medium">Connected</span>
            </div>
            <div
              className={`text-sm space-y-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              <div>
                <span
                  className={isDarkMode ? "text-gray-500" : "text-gray-400"}
                >
                  Webhook ID:{" "}
                </span>
                {status.webhookId}
              </div>
              <div>
                <span
                  className={isDarkMode ? "text-gray-500" : "text-gray-400"}
                >
                  Endpoint:{" "}
                </span>
                <span className="font-mono text-xs">{status.webhookUrl}</span>
              </div>
              {status.tokenExpiresAt && (
                <div>
                  <span
                    className={isDarkMode ? "text-gray-500" : "text-gray-400"}
                  >
                    Token refreshes:{" "}
                  </span>
                  automatically
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleDisconnect}
            disabled={loading}
            className="w-full px-4 py-3 bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-white"
          >
            {loading ? (
              <>
                <Loader size={18} className="animate-spin" />
                Disconnecting...
              </>
            ) : (
              "Disconnect Webex"
            )}
          </button>
        </div>
      ) : (
        /* Not connected state */
        <div className="space-y-4">
          <div
            className={`p-4 rounded-lg ${isDarkMode ? "bg-blue-500/10 border border-blue-500/30" : "bg-blue-50 border border-blue-200"}`}
          >
            <div className="flex items-start gap-3">
              <AlertCircle
                size={20}
                className="text-blue-400 mt-0.5 shrink-0"
              />
              <div
                className={`text-sm ${isDarkMode ? "text-blue-400" : "text-blue-700"}`}
              >
                <strong className="font-medium">How it works</strong>
                <ol
                  className={`mt-2 space-y-1 list-decimal list-inside ${isDarkMode ? "text-blue-400/80" : "text-blue-600/80"}`}
                >
                  <li>
                    Click <em>Connect Webex</em> below
                  </li>
                  <li>
                    Sign in to your Webex account and approve the requested
                    permissions
                  </li>
                  <li>
                    You'll be redirected back here — call pop-ups and logging
                    will be active immediately
                  </li>
                </ol>
                <a
                  href="https://developer.webex.com/docs/integrations"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`mt-3 inline-flex items-center gap-1 underline hover:opacity-80 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}
                >
                  Learn about Webex integrations
                  <ExternalLink size={13} />
                </a>
              </div>
            </div>
          </div>

          <button
            onClick={handleConnect}
            disabled={loading}
            className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-white"
          >
            {loading ? (
              <>
                <Loader size={18} className="animate-spin" />
                Redirecting to Webex...
              </>
            ) : (
              <>
                <Shield size={18} />
                Connect Webex
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default WebexIntegrationSettings;
